import { UI } from '../../UI.ts'
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js'
import { type AnimationClip, Scene, type SkinnedMesh, type Object3D } from 'three'
import { type DownloadSettings } from './DownloadSettings.ts'
import { ExportBoneNamingService } from './ExportBoneNamingService.ts'
import { ExportValidation } from './ExportValidation.ts'
import { ModalDialog } from '../../ModalDialog.ts'
import JSZip from 'jszip'

// Note: EventTarget is a built-in interface and do not need to import it
export class StepExportToFile extends EventTarget {
  private readonly ui: UI = UI.getInstance()
  private animation_clips_to_export: AnimationClip[] = []

  public set_animation_clips_to_export (all_animations_clips: AnimationClip[], animation_checkboxes: number[]): void {
    this.animation_clips_to_export = []
    animation_checkboxes.forEach((indx) => {
      const original_clip: AnimationClip = all_animations_clips[indx]
      const cloned_clip: AnimationClip = original_clip.clone()
      this.animation_clips_to_export.push(cloned_clip)
    })
  }

  public async export (
    skinned_meshes: SkinnedMesh[], 
    filename: string = 'exported_model',
    download_settings: DownloadSettings): Promise<void> {

    const validation_result = ExportValidation.validate_skinned_mesh_export(skinned_meshes, this.animation_clips_to_export)
    if (!validation_result.valid) {
      new ModalDialog('Export Validation', ExportValidation.to_html(validation_result)).show()
      return
    }

    const export_scene = new Scene()

    // When exporting to a file, we need to temporarily move the skinned mesh to a new scene
    // skinned meshes can only be part of one scene at a time, so we must move it back to
    // its original parent after exporting
    const original_parents = new Map<SkinnedMesh, Object3D | null>()

    skinned_meshes.forEach((final_skinned_mesh) => {
    // Save the original parent
      original_parents.set(final_skinned_mesh, final_skinned_mesh.parent)
      export_scene.add(final_skinned_mesh)
    })

    const export_clips = this.animation_clips_to_export.map((clip) => clip.clone())
    const restore_bone_names = ExportBoneNamingService.apply_download_settings(
      skinned_meshes,
      export_clips,
      download_settings.bone_naming_structure()
    )

    try {
      await this.export_glb(export_scene, export_clips, filename)
    } catch (error) {
      new ModalDialog('Export failed', format_export_error(error)).show()
    } finally {
      restore_mesh_parents(skinned_meshes, original_parents, export_scene)
      restore_bone_names()
    }
  }

  public async export_batch_zip (
    skinned_meshes: SkinnedMesh[],
    filename: string = 'animation-batch',
    download_settings: DownloadSettings
  ): Promise<void> {
    const validation_result = ExportValidation.validate_skinned_mesh_export(skinned_meshes, this.animation_clips_to_export)
    if (!validation_result.valid) {
      new ModalDialog('Export Validation', ExportValidation.to_html(validation_result)).show()
      return
    }

    const export_scene = new Scene()
    const original_parents = new Map<SkinnedMesh, Object3D | null>()
    skinned_meshes.forEach((mesh) => {
      original_parents.set(mesh, mesh.parent)
      export_scene.add(mesh)
    })

    const export_clips = this.animation_clips_to_export.map((clip) => clip.clone())
    const restore_bone_names = ExportBoneNamingService.apply_download_settings(
      skinned_meshes,
      export_clips,
      download_settings.bone_naming_structure()
    )

    try {
      const zip = new JSZip()
      for (let i = 0; i < export_clips.length; i++) {
        const clip = export_clips[i]
        const clip_name = batch_clip_filename(clip.name, i)
        const glb_blob = await this.build_glb_blob(export_scene, [clip])
        zip.file(`${clip_name}.glb`, glb_blob)
      }
      zip.file('manifest.json', JSON.stringify(create_batch_export_manifest(export_clips), null, 2))
      const zip_blob = await zip.generateAsync({ type: 'blob' })
      this.save_file(zip_blob, `${sanitize_filename(filename) || 'animation-batch'}.zip`)
    } catch (error) {
      new ModalDialog('Batch export failed', format_export_error(error)).show()
    } finally {
      restore_mesh_parents(skinned_meshes, original_parents, export_scene)
      restore_bone_names()
    }
  }

  /**
   * Export a single clip plus its thumbnail (data URL) as a ZIP. The ZIP
   * contains `<filename>.glb` and `<filename>.png`. Skips the PNG entry when
   * `thumbnail_data_url` is undefined and falls through to a raw GLB save in
   * that case (the user gets the same artifact they'd get without thumbnails).
   *
   * Used by the per-clip dispatcher in the engine when the chosen clip has a
   * captured thumbnail (user-authored clips that have been baked at least
   * once); for clips without thumbnails the existing `export()` path runs.
   */
  public async export_with_thumbnail (
    skinned_meshes: SkinnedMesh[],
    filename: string,
    download_settings: DownloadSettings,
    thumbnail_data_url: string | undefined
  ): Promise<void> {
    if (thumbnail_data_url === undefined) {
      await this.export(skinned_meshes, filename, download_settings)
      return
    }

    const validation_result = ExportValidation.validate_skinned_mesh_export(skinned_meshes, this.animation_clips_to_export)
    if (!validation_result.valid) {
      new ModalDialog('Export Validation', ExportValidation.to_html(validation_result)).show()
      return
    }

    const export_scene = new Scene()
    const original_parents = new Map<SkinnedMesh, Object3D | null>()
    skinned_meshes.forEach((mesh) => {
      original_parents.set(mesh, mesh.parent)
      export_scene.add(mesh)
    })

    const export_clips = this.animation_clips_to_export.map((clip) => clip.clone())
    const restore_bone_names = ExportBoneNamingService.apply_download_settings(
      skinned_meshes, export_clips, download_settings.bone_naming_structure()
    )
    try {
      const glb_blob = await this.build_glb_blob(export_scene, export_clips)
      const png_bytes = data_url_to_uint8_array(thumbnail_data_url)
      const zip = new JSZip()
      zip.file(`${filename}.glb`, glb_blob)
      if (png_bytes !== null) zip.file(`${filename}.png`, png_bytes)
      const zip_blob = await zip.generateAsync({ type: 'blob' })
      this.save_file(zip_blob, `${filename}.zip`)
    } catch (error) {
      new ModalDialog('Export failed', format_export_error(error)).show()
    } finally {
      // Restore original scene parenting so the live preview keeps working.
      restore_mesh_parents(skinned_meshes, original_parents, export_scene)
      restore_bone_names()
    }
  }

  /**
   * Build the GLB bytes for a scene + animations without triggering a download.
   * Returned as a Blob so the caller can package it (e.g., into a ZIP with a
   * thumbnail PNG via JSZip) before saving.
   */
  public async build_glb_blob (exported_scene: Scene, animations_to_export: AnimationClip[]): Promise<Blob> {
    return await new Promise<Blob>((resolve, reject) => {
      const gltf_exporter = new GLTFExporter()
      const export_options = { binary: true, onlyVisible: false, embedImages: true, animations: animations_to_export }
      gltf_exporter.parse(
        exported_scene,
        (result) => {
          if (result instanceof ArrayBuffer) {
            resolve(new Blob([result], { type: 'model/gltf-binary' }))
          } else {
            reject(new Error('Export result is not an ArrayBuffer'))
          }
        },
        (error) => reject(error),
        export_options
      )
    })
  }

  public async export_glb (exported_scene: Scene, animations_to_export: AnimationClip[], file_name: string): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const gltf_exporter = new GLTFExporter()

      const export_options = {
        binary: true,
        onlyVisible: false,
        embedImages: true,
        animations: animations_to_export
      }

      gltf_exporter.parse(
        exported_scene,
        (result: ArrayBuffer | { [key: string]: unknown }) => {
          // Handle the result of the export
          if (result instanceof ArrayBuffer) {
            this.save_array_buffer(result, `${file_name}.glb`)
            resolve() // Resolve the promise when the export is complete
          } else {
            reject(new Error('Export result is not an ArrayBuffer'))
          }
        },
        (error: any) => {
          reject(error) // Reject the promise if an error occurs
        },
        export_options
      )
    })
  }

  private save_file (blob: Blob, filename: string): void {
    const hidden_link = this.ui.dom_export_button_hidden_link as HTMLAnchorElement | null
    assert_export_download_link(hidden_link)
    hidden_link.href = URL.createObjectURL(blob)
    hidden_link.download = filename
    hidden_link.click()
  }

  // used for GLB to turn content into a byte array for saving
  private save_array_buffer (buffer: ArrayBuffer, filename: string): void {
    this.save_file(new Blob([buffer], { type: 'application/octet-stream' }), filename)
  }
}

/** Decode a `data:image/png;base64,...` URL into raw bytes. Returns null if
 *  the URL is malformed or not base64-encoded. */
function data_url_to_uint8_array (data_url: string): Uint8Array | null {
  const match = /^data:[^;]+;base64,(.*)$/.exec(data_url)
  if (match === null) return null
  try {
    const binary = atob(match[1])
    const out = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i)
    return out
  } catch (_) {
    return null
  }
}

function sanitize_filename (raw: string): string {
  return raw
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[._\s]+|[._\s]+$/g, '')
    .slice(0, 96)
}

export interface BatchExportManifest {
  exported_at: string
  clip_count: number
  clips: string[]
}

export function create_batch_export_manifest (clips: AnimationClip[]): BatchExportManifest {
  return {
    exported_at: new Date().toISOString(),
    clip_count: clips.length,
    clips: clips.map((clip) => clip.name)
  }
}

export function batch_clip_filename (clip_name: string, index: number): string {
  return sanitize_filename(clip_name) || `clip-${index + 1}`
}

export function format_export_error (error: unknown): string {
  if (error instanceof Error && error.message.trim() !== '') return error.message
  if (typeof error === 'string' && error.trim() !== '') return error
  return 'The exporter could not create the file. Check the selected clips and try again.'
}

export function assert_export_download_link (link: HTMLAnchorElement | null): asserts link is HTMLAnchorElement {
  if (link === null) throw new Error('Export download link is not available.')
}

function restore_mesh_parents (
  skinned_meshes: SkinnedMesh[],
  original_parents: Map<SkinnedMesh, Object3D | null>,
  export_scene: Scene
): void {
  skinned_meshes.forEach((mesh) => {
    const parent = original_parents.get(mesh)
    if (parent != null) parent.add(mesh)
    else export_scene.remove(mesh)
  })
}
